import { useState, useCallback } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import AppBar from '@mui/material/AppBar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import BackIcon from '@mui/icons-material/ArrowBack';
import Toolbar from '@mui/material/Toolbar';

import DeleteButton from '@mui/icons-material/DeleteOutlined';

import Spacings from '~/modules/ui-kit/spacings';
import ContentView from '~/components/ContentView';
import DepotForm from '~/components/DepotForm';
import ConfirmationModal from '~/components/ConfirmationModal';
import { useSnackbar } from '~/containers/Snackbar/SnackbarContext';
import messages from './messages';
import styles from './styles.module.css';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import { useDepot } from '~/hooks/api/useDepot';
import { useEditDepot } from '~/hooks/api/useEditDepot';
import { useTheme } from '@mui/material';
import { useDeleteDepot } from '~/hooks/api/useDeleteDepot';

const route = getRouteApi('/depots/$depotId');

const DepotEdit = () => {
  const params = route.useParams();
  const search = route.useSearch();
  const theme = useTheme();
  const navigate = useNavigate();
  const depotId = parseInt(params.depotId, 10);
  const intl = useIntl();
  const { showSnackbar } = useSnackbar();
  const { data: depot, isFetching } = useDepot(depotId);

  const handleBackButton = useCallback(() => {
    navigate({ to: search.from || '/depots' });
  }, [navigate, search.from]);

  const depotMutation = useEditDepot(depotId, {
    onSuccess: () => {
      showSnackbar({
        message: intl.formatMessage(messages.saveSuccess),
      });
      handleBackButton();
    },
    onError: () => {
      showSnackbar({
        message: intl.formatMessage(messages.saveFailure),
      });
    },
  });
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const toggleDeleteModal = useCallback(() => {
    setDeleteModalOpen((prev) => !prev);
  }, []);

  const deleteDepotMutation = useDeleteDepot({
    onSuccess: () => {
      showSnackbar({
        message: intl.formatMessage(messages.deleteSuccess),
      });
      handleBackButton();
    },
    onError: () => {
      showSnackbar({
        message: intl.formatMessage(messages.deleteFailure),
      });
      toggleDeleteModal();
    },
  });

  return (
    <ContentView
      header={
        <AppBar position="static">
          <Toolbar className={styles.header}>
            <IconButton color="inherit" onClick={handleBackButton} size="large">
              <BackIcon data-testid="back" />
            </IconButton>
            <Typography
              variant="h6"
              style={{ flexGrow: 1, marginLeft: theme.spacing(2) }}
              sx={{
                color: 'inherit',
              }}
            >
              <FormattedMessage {...messages.title} />
            </Typography>
            <Spacings.Inline>
              <IconButton
                color="inherit"
                onClick={toggleDeleteModal}
                size="large"
              >
                <DeleteButton data-testid="delete" />
              </IconButton>
            </Spacings.Inline>
          </Toolbar>
        </AppBar>
      }
      isLoading={isFetching}
    >
      {!isFetching && depot && (
        <DepotForm
          onSubmit={depotMutation.mutate}
          initialValues={{
            name: depot.name,
            address: depot.address || undefined,
            city: depot.city || undefined,
            zip: depot.zip || undefined,
          }}
        />
      )}
      <ConfirmationModal
        open={deleteModalOpen}
        title={<FormattedMessage {...messages.deleteConfirmTitle} />}
        text={<FormattedMessage {...messages.deleteConfirmText} />}
        confirmLabel={<FormattedMessage {...messages.deleteConfirmDelete} />}
        cancelLabel={<FormattedMessage {...messages.deleteConfirmCancel} />}
        onCancel={toggleDeleteModal}
        onConfirm={() => deleteDepotMutation.mutate(depotId)}
      />
    </ContentView>
  );
};

export default DepotEdit;
